/**
 * The 7 Vitality cards (PLAN-FASE2.md § WP-02) and the static thresholds
 * that decide each one's level badge (Goed/Kans) and which HR-action
 * suggestion to show. No AI call in this WP (see *Openstaande punten* —
 * "AI-samenvattingen" is a separate, later decision) — every suggestion
 * below is a fixed template picked by a metric-threshold, not a generated
 * text.
 *
 * Metric keys (`sleep_duration`, `steps`, `heart_rate`, `hrv`, `stress`) are
 * the ones `php/stats_metrics.php` already defines — "4 Ja" in the plan's
 * step table. `vitalityScore` and `recovery` have no metric of their own
 * ("5 Nee"): both are a deterministic composite of the other four, badged
 * `aiDerived` because they are computed rather than measured, not because an
 * LLM is involved.
 */

import { seriesStroke, type ThemeColors } from '@/lib/useThemeColors';

export type CardId =
  'vitalityScore' | 'sleep' | 'stress' | 'recovery' | 'activity' | 'hrv' | 'restingHeartRate';

export type MetricBadge = 'measured' | 'aiDerived';
export type Level = 'good' | 'opportunity';

export const CARD_IDS: readonly CardId[] = [
  'vitalityScore',
  'sleep',
  'stress',
  'recovery',
  'activity',
  'hrv',
  'restingHeartRate',
];

export const CARD_BADGE: Record<CardId, MetricBadge> = {
  vitalityScore: 'aiDerived',
  sleep: 'measured',
  stress: 'measured',
  recovery: 'aiDerived',
  activity: 'measured',
  hrv: 'measured',
  // A proxy, not a direct reading (see `api.ts`'s `restingHeartRateFromSeries`
  // doc comment for the chosen approach) — still built straight from
  // measured heart_rate samples, not a template, so it keeps the "measured"
  // badge rather than "aiDerived".
  restingHeartRate: 'measured',
};

/** Whether a lower raw value is the *better* direction — stress and resting
 * heart rate improve by going down, everything else by going up. */
export const LOWER_IS_BETTER: Record<CardId, boolean> = {
  vitalityScore: false,
  sleep: false,
  stress: true,
  recovery: false,
  activity: false,
  hrv: false,
  restingHeartRate: true,
};

/**
 * The threshold each raw value is measured against for its level badge.
 * `good` holds when the value is at or beyond the threshold in the
 * "better" direction (`LOWER_IS_BETTER` decides which way that is).
 * Numbers are a starting point (documented here, not hidden in a
 * component) — WP-08 (Instellingen) is where these become configurable per
 * company, per *Openstaande punten*.
 */
export const GOOD_THRESHOLD: Record<CardId, number> = {
  vitalityScore: 60, // composite score, 0-100
  sleep: 7 * 3600, // seconds/day average
  stress: 40, // score, 0-100, lower is calmer
  recovery: 60, // composite score, 0-100
  activity: 7000, // steps/day average
  hrv: 40, // ms
  restingHeartRate: 65, // bpm
};

export function levelFor(cardId: CardId, value: number | null): Level | null {
  if (value === null || Number.isNaN(value)) return null;
  const threshold = GOOD_THRESHOLD[cardId];
  const isGood = LOWER_IS_BETTER[cardId] ? value <= threshold : value >= threshold;
  return isGood ? 'good' : 'opportunity';
}

/** A stable index per card, passed to `seriesStroke()` as its generic-cycle
 * fallback — in practice every card below resolves through `CARD_METRIC_KEY`
 * or `CARD_DIRECT_COLOR` instead, so this index is never actually used to
 * pick a color, only kept as the required positional argument. */
export const CARD_COLOR_INDEX: Record<CardId, number> = {
  vitalityScore: 0,
  sleep: 1,
  stress: 2,
  recovery: 3,
  activity: 4,
  hrv: 5,
  restingHeartRate: 6,
};

/** Card -> underlying metric key, so `seriesStroke()`'s per-metric palette
 * (`METRIC_COLOR` in `src/lib/useThemeColors.ts`) gives each metric-backed
 * card its own fixed, distinct color instead of falling into the generic
 * index-cycling fallback. The two composite cards (`vitalityScore`,
 * `recovery`) have no metric of their own — see `CARD_DIRECT_COLOR` below. */
export const CARD_METRIC_KEY: Partial<Record<CardId, string>> = {
  sleep: 'sleep_duration',
  stress: 'stress',
  activity: 'steps',
  hrv: 'hrv',
  restingHeartRate: 'heart_rate',
};

/** Direct `ThemeColors` token for the two composite cards, which have no
 * metric key to resolve through `METRIC_COLOR` — matches
 * `OverviewPage.tsx`'s own already-shipped `DailySeriesTrendCard` colors for
 * these same two composites (`color={colors.mintDeep}` for vitalityScore,
 * `color={colors.info}` for recovery) rather than picking new ones, so the
 * same card reads as the same color on both pages. Note this app's palette
 * is mostly shades of green by design (`--accent`/`--mint-deep`/`--pine` are
 * all green in dark mode, barely distinguishable from one another) — true
 * hue variety only exists across `mintDeep`(green)/`warning`(amber)/
 * `info`(blue)/`danger`(red); `CARD_METRIC_KEY` below already spends those
 * four across sleep/stress/hrv/activity/restingHeartRate the same way
 * Overview's own trend charts do, so some reuse across the 7 cards (e.g.
 * `hrv` and `recovery` both `info`) is expected and intentional, not a bug —
 * it's the same overlap Overview already has. */
export const CARD_DIRECT_COLOR: Partial<Record<CardId, keyof ThemeColors>> = {
  vitalityScore: 'mintDeep',
  recovery: 'info',
};

/** The single source of truth for "what color is this card's dot/line" —
 * `VitalityCard.tsx` uses this for both the header's colored dot and the
 * `Sparkline`'s stroke, so they can never drift apart. */
export function cardStroke(colors: ThemeColors, cardId: CardId): string {
  const direct = CARD_DIRECT_COLOR[cardId];
  if (direct) return colors[direct];
  return seriesStroke(colors, CARD_COLOR_INDEX[cardId], CARD_METRIC_KEY[cardId]).stroke;
}

export type TrendDirection = 'up' | 'down' | 'flat';

/** Categorical trend direction from a raw value change — "up"/"down" mean
 * the literal direction of the number, not good/bad (`vitality.trend.*`
 * picks the label per card via `LOWER_IS_BETTER`). Mirrors
 * `VitalityCard.tsx`'s `TrendIndicator` flat-threshold. */
export function trendDirection(change: number | null): TrendDirection {
  if (change === null || Math.abs(change) < 0.01) return 'flat';
  return change > 0 ? 'up' : 'down';
}

export type StatusPill = 'healthy' | 'opportunity' | 'strongForward';

/**
 * The header's long-form status pill (next to the "Gemeten"/"AI-inzicht"
 * badge) — a friendlier restatement of `level`, with one special case:
 * activity that is both good and still improving reads as "Sterk &
 * vooruit" rather than the generic "Gezond niveau" every other good card
 * gets, since an already-healthy step count still climbing is worth
 * calling out differently from one that's merely steady.
 */
export function statusPillFor(
  cardId: CardId,
  level: Level | null,
  direction: TrendDirection,
): StatusPill | null {
  if (level === null) return null;
  if (level === 'opportunity') return 'opportunity';
  const improving = LOWER_IS_BETTER[cardId] ? direction === 'down' : direction === 'up';
  if (cardId === 'activity' && improving) return 'strongForward';
  return 'healthy';
}

/** Composite score (0-100) from already-normalized 0-100 inputs — a simple
 * mean, not a weighted model (nothing here claims to be validated science,
 * only a deterministic placeholder until the real AI-samenvatting work,
 * see *Openstaande punten*). */
export function compositeScore(normalized: readonly (number | null)[]): number | null {
  const known = normalized.filter((v): v is number => v !== null);
  if (known.length === 0) return null;
  return known.reduce((sum, v) => sum + v, 0) / known.length;
}

/** Clamped 0-100 normalization of one raw value against its own good
 * threshold — used to build `vitalityScore`/`recovery`. */
export function normalize(cardId: CardId, value: number | null): number | null {
  if (value === null || Number.isNaN(value)) return null;
  const threshold = GOOD_THRESHOLD[cardId];
  if (LOWER_IS_BETTER[cardId]) {
    // 0 is best-case floor, 2x threshold is worst-case ceiling.
    const ratio = 1 - value / (threshold * 2);
    return Math.max(0, Math.min(100, ratio * 100));
  }
  const ratio = value / threshold;
  return Math.max(0, Math.min(100, ratio * 100));
}
